// Web Analytics Utility using Firebase
import { FirebaseConfig } from '../config/FirebaseConfig';

// Initialize Firebase Analytics for Web
let analytics = null;

// Initialize analytics when Firebase is available
const initializeAnalytics = async () => {
  try {
    // Dynamic import to avoid compatibility issues
    const { initializeApp } = await import('firebase/app');
    const { getAnalytics, logEvent, setUserId, setUserProperty } = await import('firebase/analytics');
    
    const app = initializeApp(FirebaseConfig);
    analytics = getAnalytics(app);
    
    console.log('✅ Firebase Analytics initialized for web');
    return analytics;
  } catch (error) {
    console.error('❌ Failed to initialize Firebase Analytics:', error);
    return null;
  }
};

// Analytics Service Class for Web
class WebAnalyticsService {
  constructor() {
    this.analytics = null;
    this.isInitialized = false;
  }

  async init() {
    if (!this.isInitialized) {
      this.analytics = await initializeAnalytics();
      this.isInitialized = true;
    }
    return this.analytics;
  }

  // Enable/Disable Analytics Collection
  setAnalyticsCollectionEnabled(enabled) {
    if (this.analytics) {
      // Web analytics collection is typically always enabled
      console.log(`Analytics collection ${enabled ? 'enabled' : 'disabled'}`);
    }
  }

  // Set User Properties
  setUserProperty(name, value) {
    if (this.analytics) {
      try {
        const { setUserProperty: setProp } = require('firebase/analytics');
        setProp(this.analytics, name, value);
      } catch (error) {
        console.error('Error setting user property:', error);
      }
    }
  }

  // Set User ID
  setUserId(userId) {
    if (this.analytics) {
      try {
        const { setUserId: setUid } = require('firebase/analytics');
        setUid(this.analytics, userId);
      } catch (error) {
        console.error('Error setting user ID:', error);
      }
    }
  }

  // Track Screen Views
  logScreenView(screenName, screenClass) {
    this.logEvent('screen_view', {
      screen_name: screenName,
      screen_class: screenClass,
    });
  }

  // Driver Events
  logDriverHome() {
    this.logEvent('driver_home', {
      page_title: 'driver_home',
      user_type: 'driver',
      timestamp: new Date().toISOString(),
    });
  }

  logDriverSignup(driverData = {}) {
    this.logEvent('driver_signup', {
      user_type: 'driver',
      registration_method: driverData.method || 'web',
      has_vehicle: driverData.hasVehicle || false,
      has_license: driverData.hasLicense || false,
      timestamp: new Date().toISOString(),
      ...driverData,
    });
  }

  // Passenger Events
  logPassengerHome() {
    this.logEvent('passenger_home', {
      page_title: 'passenger_home',
      user_type: 'passenger',
      timestamp: new Date().toISOString(),
    });
  }

  logPassengerSignup(passengerData = {}) {
    this.logEvent('passenger_signup', {
      user_type: 'passenger',
      registration_method: passengerData.method || 'web',
      has_payment_method: passengerData.hasPaymentMethod || false,
      timestamp: new Date().toISOString(),
      ...passengerData,
    });
  }

  // Ride Events
  logRideRequested(rideData = {}) {
    this.logEvent('ride_requested', {
      user_type: rideData.userType || 'passenger',
      ride_id: rideData.rideId || null,
      pickup_location: rideData.pickupLocation || null,
      destination_location: rideData.destinationLocation || null,
      estimated_fare: rideData.estimatedFare || null,
      currency: rideData.currency || 'USD',
      payment_method: rideData.paymentMethod || null,
      timestamp: new Date().toISOString(),
      ...rideData,
    });
  }

  logRideCompleted(rideData = {}) {
    this.logEvent('ride_completed', {
      user_type: rideData.userType || 'passenger',
      ride_id: rideData.rideId || null,
      actual_fare: rideData.actualFare || null,
      currency: rideData.currency || 'USD',
      distance_km: rideData.distanceKm || null,
      duration_minutes: rideData.durationMinutes || null,
      payment_method: rideData.paymentMethod || null,
      timestamp: new Date().toISOString(),
      ...rideData,
    });
  }

  logRideCancelled(rideData = {}) {
    this.logEvent('cancel_ride', {
      user_type: rideData.userType || 'passenger',
      ride_id: rideData.rideId || null,
      cancellation_reason: rideData.cancellationReason || null,
      time_to_cancellation_minutes: rideData.timeToCancellationMinutes || null,
      timestamp: new Date().toISOString(),
      ...rideData,
    });
  }

  // Rating Events
  logDriverRating(ratingData = {}) {
    this.logEvent('rate_driver', {
      user_type: 'passenger',
      ride_id: ratingData.rideId || null,
      driver_id: ratingData.driverId || null,
      rating: ratingData.rating || 0,
      rating_comment: ratingData.comment || null,
      timestamp: new Date().toISOString(),
      ...ratingData,
    });
  }

  logPassengerRating(ratingData = {}) {
    this.logEvent('rate_passenger', {
      user_type: 'driver',
      ride_id: ratingData.rideId || null,
      passenger_id: ratingData.passengerId || null,
      rating: ratingData.rating || 0,
      rating_comment: ratingData.comment || null,
      timestamp: new Date().toISOString(),
      ...ratingData,
    });
  }

  // Registration Funnel Events
  logRegisterButtonClick(userType, screenName) {
    this.logEvent('click_register_button', {
      user_type: userType,
      screen_name: screenName,
      timestamp: new Date().toISOString(),
    });
  }

  logRegisterScreenView(userType, screenName) {
    this.logEvent('view_register_screen', {
      user_type: userType,
      screen_name: screenName,
      timestamp: new Date().toISOString(),
    });
  }

  // Promo Events
  logPromoApplied(promoData = {}) {
    this.logEvent('promo_applied', {
      user_type: promoData.userType || 'passenger',
      promo_code: promoData.promoCode || null,
      discount_amount: promoData.discountAmount || null,
      currency: promoData.currency || 'USD',
      ride_id: promoData.rideId || null,
      timestamp: new Date().toISOString(),
      ...promoData,
    });
  }

  // App Update Event
  logAppUpdate(updateData = {}) {
    this.logEvent('app_update', {
      previous_version: updateData.previousVersion || null,
      new_version: updateData.newVersion || null,
      update_type: updateData.updateType || 'automatic',
      timestamp: new Date().toISOString(),
      ...updateData,
    });
  }

  // Custom Events
  logCustomEvent(eventName, parameters = {}) {
    this.logEvent(eventName, {
      timestamp: new Date().toISOString(),
      ...parameters,
    });
  }

  // E-commerce Events (for ride purchases)
  logPurchase(purchaseData = {}) {
    this.logEvent('purchase', {
      user_type: purchaseData.userType || 'passenger',
      ride_id: purchaseData.rideId || null,
      value: purchaseData.value || 0,
      currency: purchaseData.currency || 'USD',
      payment_method: purchaseData.paymentMethod || null,
      timestamp: new Date().toISOString(),
      ...purchaseData,
    });
  }

  // Session Events
  logSessionStart(sessionData = {}) {
    this.logEvent('session_start', {
      user_type: sessionData.userType || 'guest',
      is_first_session: sessionData.isFirstSession || false,
      timestamp: new Date().toISOString(),
      ...sessionData,
    });
  }

  // Error Tracking
  logError(errorData = {}) {
    this.logEvent('app_error', {
      error_type: errorData.errorType || 'unknown',
      error_message: errorData.errorMessage || null,
      screen_name: errorData.screenName || null,
      user_type: errorData.userType || 'guest',
      timestamp: new Date().toISOString(),
      ...errorData,
    });
  }

  // Core logging method
  logEvent(eventName, parameters = {}) {
    if (this.analytics) {
      try {
        const { logEvent: logEvt } = require('firebase/analytics');
        logEvt(this.analytics, eventName, parameters);
        console.log(`📊 Analytics Event: ${eventName}`, parameters);
      } catch (error) {
        console.error('Error logging event:', error);
        // Fallback to console for debugging
        console.log(`📊 [Analytics] ${eventName}:`, parameters);
      }
    } else {
      // Fallback when analytics is not initialized
      console.log(`📊 [Analytics] ${eventName}:`, parameters);
    }
  }
}

// Create singleton instance
const webAnalyticsService = new WebAnalyticsService();

export default webAnalyticsService;
